import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandS, BrandXS } from '@sorare/core/src/atoms/typography';
import { Gauge } from '@sorare/core/src/atoms/ui/Gauge';
import { useAmountWithConversion } from '@sorare/core/src/hooks/useAmountWithConversion';
import { useMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount';
import { transferMarket } from '@sorare/core/src/lib/glossary';
import { monetaryAmountFragment } from '@sorare/core/src/lib/monetaryAmount';

import { getSteppedPrimaryOffer } from '#src/hooks/primaryBuy/steppedPrimaryOffer.ts';

import type { PrimaryOfferStockCounter_primaryOffer } from './__generated__/index.graphql.ts';

const Root = styled(Vertical).attrs({ gap: 0.5 })`
  width: 100%;
`;

type Props = {
  primaryOffer: PrimaryOfferStockCounter_primaryOffer;
  size?: 'regular' | 'compact';
};

export const PrimaryOfferStockCounter = ({
  primaryOffer,
  size = 'regular',
}: Props) => {
  const { toMonetaryAmount } = useMonetaryAmount();
  const { main } = useAmountWithConversion({
    monetaryAmount: toMonetaryAmount(primaryOffer.price),
  });

  const { isStepped, remaining, ratio, color } =
    getSteppedPrimaryOffer(primaryOffer);

  if (!isStepped || remaining == null || remaining <= 0) return null;

  const label = (
    <FormattedMessage
      {...transferMarket.leftAtThisPrice}
      values={{
        count: remaining,
        price: main,
        remaining: (
          <span style={{ color }}>
            <FormattedNumber value={remaining} />
          </span>
        ),
      }}
    />
  );

  return (
    <Root>
      {ratio != null && (
        <Gauge
          percentage={`${ratio * 100}%`}
          color={color}
          bgColor="var(--c-nd-200)"
          height="var(--unit)"
          bgRadius="var(--unit)"
        />
      )}
      {size === 'compact' ? (
        <BrandXS color="var(--c-onBase-secondary)">{label}</BrandXS>
      ) : (
        <BrandS color="var(--c-onBase-secondary)">{label}</BrandS>
      )}
    </Root>
  );
};

PrimaryOfferStockCounter.fragments = {
  primaryOffer: gql`
    fragment PrimaryOfferStockCounter_primaryOffer on TokenPrimaryOffer {
      id
      ...SteppedPrimaryOffer_primaryOffer
      price {
        ...MonetaryAmountFragment_monetaryAmount
      }
    }
    ${getSteppedPrimaryOffer.fragments.primaryOffer}
    ${monetaryAmountFragment}
  ` as TypedDocumentNode<PrimaryOfferStockCounter_primaryOffer>,
};
